import ArrowDownIcon from '@images/icons/chevron-down-black.svg?react'
import ArrowUpIcon from '@images/icons/chevron-up-black.svg?react'
import { useOutsideClick } from 'mmfintech-commons'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import './compactAmountDropdown.scss'

// Option interface with per-option helper text
export interface AmountOptionWithHelper {
  label: string
  value: string
  caption?: string
  helperText?: string | ReactNode
  helperTextColor?: 'green' | 'red' | 'yellow' | string
  showHelper?: boolean
}

interface CompactAmountDropdownProps {
  options: Array<AmountOptionWithHelper>
  selectedValue?: string
  onSelect: (value: string) => void
  isLoading?: boolean
}

/**
 * CompactAmountDropdown - A standalone compact dropdown for amount selection
 *
 * Built from scratch with NO dependency on CoreSelect.
 * This component provides a minimal, icon-only dropdown button that expands to show
 * amount options with labels and captions. Designed specifically for space-constrained
 * layouts like the overdraft repay modal.
 *
 * Features:
 * - Compact 4rem width button (icon only, no text)
 * - Full-width dropdown options (25-35rem) with label + caption
 * - Fixed positioning to prevent modal overflow
 * - Always visible (doesn't hide after selection)
 * - Click toggle behavior (open/close on arrow click)
 * - Click outside to close
 */
export const CompactAmountDropdown = ({ options, selectedValue, onSelect }: CompactAmountDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useOutsideClick(() => {
    setIsOpen(false)
  })

  // Calculate dropdown position
  const calculatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      // Position dropdown to the left, aligning right edge with button's right edge
      // Using dynamic width (min-width: 30rem, max-width: 50rem, width: max-content)
      setDropdownPosition({
        top: rect.bottom + 20, // 20px gap below button
        left: rect.right - 320 // Shift dropdown to the left (320px)
      })
    }
  }

  // Calculate position when opening
  useEffect(() => {
    if (isOpen) {
      calculatePosition()
    }
  }, [isOpen])

  // Recalculate position on window resize
  useEffect(() => {
    if (!isOpen) return

    const handleResize = () => {
      calculatePosition()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleOptionClick = (value: string) => {
    onSelect(value)
    setIsOpen(false)
  }

  return (
    <div className='compact-amount-dropdown' ref={dropdownRef}>
      {/* Compact Button (icon only) */}
      <div ref={buttonRef} className='compact-dropdown-button' onClick={handleToggle}>
        {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
      </div>

      {/* Dropdown Options */}
      {isOpen && dropdownPosition && (
        <div
          className='compact-dropdown-options'
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
          role='listbox'
        >
          {options.map(option => (
            <div
              key={option.value}
              className={`compact-dropdown-option ${selectedValue === option.value ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option.value)}
            >
              <div className='option-content'>
                {option.caption && <div className='option-caption'>{option.caption}</div>}
                <div className='option-caption-wrapper'>
                  <div className='option-label'>{option.label}</div>
                  {/* Per-option helper text */}
                  {option.showHelper && option.helperText && (
                    <div className={`option-helper-text ${option.helperTextColor || ''}`}>{option.helperText}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
